import { SideNavigation } from '@/pages/index/_comp/sidenav/sideNavigation';
import Header from '@/comp/header/header';
import { useContext, useEffect, useLayoutEffect, useMemo } from 'react';
import Footer from '@/comp/footer/Footer';
import { AppContext } from '@/context/AppContext';
import { getRegion } from '@/_helper/bookingDetailsHelper';
import StatusPage from '@/comp/statusPage/statusPage';
import FloatingHelp from '@/comp/floatingHelp/floatingHelp';
import { checkTripEnds } from '@/_helper/checkTripStarts';
import { Icon } from '@/comp/icon/icon';
import BookingDetails from '@/pages/booking-details';
import InfoCard from '@/comp/ui/infoCard/infoCard';
import { Outlet, useLocation } from 'react-router';
import useNavigationList from '@/hooks/use_navigation_list.hook';

const HomeLayout = () => {
    const { bookingState, constants, bookingData, travelType } = useContext(AppContext);
    const location = useLocation();

    useEffect(() => {
        document.querySelector('html')?.classList.add('redesign');
    }, []);

    useLayoutEffect(() => {
        const changeView = () => {
            const timeout = setTimeout(() => {
                if (window.innerWidth >= 920 && window.$browserInfo.device === 'mobile') {
                    window.location.reload();
                }
                if (window.innerWidth <= 920 && window.$browserInfo.device === 'not-mobile') {
                    window.location.reload();
                }
            }, 200);
            return () => clearTimeout(timeout);
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', changeView);
            return () => {
                window.removeEventListener('resize', changeView);
            };
        }
    }, []);

    const alertCount = useMemo(() => {
        const data = bookingData?.data;
        // set alert object : count by sidebar navigation list entry
        let alertCount = {
            // manageBooking
            // -> alertApisData, haveToFlightAlternativesFromMail, haveToFTCRebooking, isFlightCancelledInbound || isFlightCancelledOutbound
            manageBooking:
                bookingState === 'OK' &&
                [
                    data?.alertApisData,
                    data?.haveToFlightAlternativesFromMail,
                    data?.haveToFTCRebooking,
                    data?.isFlightCancelledInbound || data?.isFlightCancelledOutbound,
                ].filter((o) => o).length,
            payment:
                bookingState === 'OK' &&
                [
                    data?.allowedInteractions?.payment?.alertFull || data?.allowedInteractions?.payment?.alertDeposit,
                ].filter((o) => o).length,
        };

        // set alert total count
        let alertTotalCount = 0;
        Object.values(alertCount).forEach((value) => {
            if (value) {
                alertTotalCount += value;
            }
        });

        return {
            alertCount: alertCount,
            alertTotalCount: alertTotalCount,
        };
    }, [bookingData?.data, bookingState]);

    const navigationList = useNavigationList(alertCount);

    useEffect(() => {
        const currentPath = location.pathname.replace('/', '');
        const currentNavItem = navigationList?.find((e) => e.name === currentPath);
        const pageTitle = currentNavItem?.label ?? 'Buchungsübersicht';
        const alertTotalCount = alertCount?.alertTotalCount ?? 0;
        const documentTitle = `${pageTitle}${window.$documentTitle}`;
        document.title = alertTotalCount > 0 ? `(${alertTotalCount}) ${documentTitle}` : documentTitle;
    }, [location.pathname, navigationList, alertCount?.alertTotalCount]);

    const allowFloatingHelp = useMemo(() => {
        const allowedTravelTypes = ['package', 'hotel'];
        return !checkTripEnds() && allowedTravelTypes.includes(travelType);
    }, [travelType]);

    function bookingDetails() {
        // affiliate
        if (window.$affiliate) {
            return false;
        }

        switch (bookingData?.data.serviceInformation.package.state) {
            case 'OK':
            case 'MO':
            case 'XX':
            case 'TB':
                return <BookingDetails bookingData={bookingData} bookingState={bookingState} />;
            case 'SB':
            case 'RQ':
                return (
                    <InfoCard type="large">
                        <p>
                            Ihre Buchung wird gerade überprüft. Bitte loggen Sie sich zu einem späteren Zeitpunkt noch
                            einmal in den Kundenbereich ein. Danke.
                        </p>
                    </InfoCard>
                );
            default:
                break;
        }
    }

    if (!bookingState || !bookingData) {
        return null;
    }

    if (bookingState === 'NO') {
        return (
            <div className="page-index">
                <Header />
                <div className="content-wrapper booking-no">
                    <div className="booking-info">
                        <h1>
                            {constants?.MY_TRIP} {getRegion(bookingData.data)}
                        </h1>
                        <span className="booking-number">Buchungsnummer {bookingData.bookingId}</span>
                    </div>
                </div>
                <StatusPage type="warning" subPage="index" bookingState={bookingState} />
            </div>
        );
    }

    return (
        <div className="page-index">
            <Header />
            <div className="content-wrapper">
                <SideNavigation currentStep={0} alert={alertCount}>
                    {window.$browserInfo.device === 'mobile' && (
                        <div className="booking-details">
                            {/* booking cancelled */}
                            {bookingState === 'XX' && (
                                <p className="booking-alert status-xx">
                                    <Icon name="freeCancellation" size={32} ariaHidden />
                                    Diese Reise wurde storniert. Weitere Informationen haben Sie per E-Mail erhalten.
                                    Bitte wenden Sie sich bei offenen Fragen an das AurumTours Serviceteam.
                                </p>
                            )}

                            {/* booking okay */}
                            {bookingState !== 'XX' && bookingDetails()}
                        </div>
                    )}
                </SideNavigation>
                <Outlet key={location.pathname} />
            </div>

            {/* Floating Help */}
            {allowFloatingHelp && <FloatingHelp />}

            <Footer />
        </div>
    );
};
export default HomeLayout;
